/**
 * Utilities for parsing and displaying AI model names in a user-friendly format.
 */

/**
 * Known model provider mappings for extracting the actual provider from model IDs.
 * This handles cases like "openrouter/anthropic/claude-sonnet-4" where the actual
 * provider is "anthropic", not "openrouter".
 */
const PROVIDER_ALIASES: Record<string, string> = {
  'anthropic': 'anthropic',
  'openai': 'openai',
  'deepseek': 'deepseek',
  'google': 'google',
  'meta-llama': 'meta',
  'meta': 'meta',
  'mistralai': 'mistral',
  'mistral': 'mistral',
  'cohere': 'cohere',
  'x-ai': 'xai',
  'xai': 'xai',
  'perplexity': 'perplexity',
  'qwen': 'qwen',
  'alibaba': 'qwen',
  '01-ai': 'yi',
  'yi': 'yi',
  'nvidia': 'nvidia',
  'amazon': 'amazon',
  'ai21': 'ai21',
  'arcee-ai': 'arcee',
  'inflection': 'inflection',
  'databricks': 'databricks',
  'microsoft': 'microsoft',
  'nous': 'nous',
  'nousresearch': 'nous',
  'together': 'together',
  'fireworks': 'fireworks',
  'groq': 'groq',
  'replicate': 'replicate',
  'huggingface': 'huggingface',
  'moonshot': 'moonshot',
  'zhipu': 'zhipu',
  'baichuan': 'baichuan',
  'minimax': 'minimax',
  'prime-intellect': 'prime-intellect',
};

/**
 * Model name transformations to create friendly display names.
 * Maps raw model ID patterns to human-readable names.
 */
const MODEL_NAME_PATTERNS: Array<{ pattern: RegExp; transform: (match: RegExpMatchArray) => string }> = [
  // Claude models
  { pattern: /^claude-sonnet-4-(\d+)$/i, transform: (m) => `Claude Sonnet 4 (${m[1]})` },
  { pattern: /^claude-sonnet-4$/i, transform: () => 'Claude Sonnet 4' },
  { pattern: /^claude-(\d+)-(\d+)-sonnet.*$/i, transform: (m) => `Claude ${m[1]}.${m[2]} Sonnet` },
  { pattern: /^claude-opus-4-(\d+)$/i, transform: (m) => `Claude Opus 4 (${m[1]})` },
  { pattern: /^claude-opus-4$/i, transform: () => 'Claude Opus 4' },
  { pattern: /^claude-(\d+)-(\d+)-opus.*$/i, transform: (m) => `Claude ${m[1]}.${m[2]} Opus` },
  { pattern: /^claude-(\d+)-(\d+)-haiku.*$/i, transform: (m) => `Claude ${m[1]}.${m[2]} Haiku` },
  { pattern: /^claude-(\d+)-opus.*$/i, transform: (m) => `Claude ${m[1]} Opus` },
  { pattern: /^claude-(\d+)-sonnet.*$/i, transform: (m) => `Claude ${m[1]} Sonnet` },
  { pattern: /^claude-(\d+)-haiku.*$/i, transform: (m) => `Claude ${m[1]} Haiku` },
  { pattern: /^claude-instant.*$/i, transform: () => 'Claude Instant' },
  
  // GPT models
  { pattern: /^gpt-4\.5-preview.*$/i, transform: () => 'GPT-4.5 Preview' },
  { pattern: /^gpt-4o-mini.*$/i, transform: () => 'GPT-4o Mini' },
  { pattern: /^gpt-4o-audio.*$/i, transform: () => 'GPT-4o Audio' },
  { pattern: /^gpt-4o-(\d{4}-\d{2}-\d{2})$/i, transform: (m) => `GPT-4o (${m[1]})` },
  { pattern: /^gpt-4o$/i, transform: () => 'GPT-4o' },
  { pattern: /^gpt-4-turbo.*$/i, transform: () => 'GPT-4 Turbo' },
  { pattern: /^gpt-4-vision.*$/i, transform: () => 'GPT-4 Vision' },
  { pattern: /^gpt-4-(\d{4}).*$/i, transform: (m) => `GPT-4 (${m[1]})` },
  { pattern: /^gpt-4$/i, transform: () => 'GPT-4' },
  { pattern: /^gpt-3\.5-turbo.*$/i, transform: () => 'GPT-3.5 Turbo' },
  { pattern: /^chatgpt-4o-latest$/i, transform: () => 'ChatGPT-4o Latest' },
  { pattern: /^o1-preview.*$/i, transform: () => 'o1 Preview' },
  { pattern: /^o1-mini.*$/i, transform: () => 'o1 Mini' },
  { pattern: /^o1-pro.*$/i, transform: () => 'o1 Pro' },
  { pattern: /^o1$/i, transform: () => 'o1' },
  { pattern: /^o3-mini.*$/i, transform: () => 'o3 Mini' },
  { pattern: /^o3$/i, transform: () => 'o3' },
  { pattern: /^o4-mini.*$/i, transform: () => 'o4 Mini' },
  
  // DeepSeek models
  { pattern: /^deepseek-chat$/i, transform: () => 'DeepSeek Chat' },
  { pattern: /^deepseek-coder.*$/i, transform: () => 'DeepSeek Coder' },
  { pattern: /^deepseek-reasoner$/i, transform: () => 'DeepSeek Reasoner' },
  { pattern: /^deepseek-r1.*$/i, transform: () => 'DeepSeek R1' },
  { pattern: /^deepseek-v(\d+).*$/i, transform: (m) => `DeepSeek V${m[1]}` },
  
  // Gemini models
  { pattern: /^gemini-2\.5-flash.*$/i, transform: () => 'Gemini 2.5 Flash' },
  { pattern: /^gemini-2\.5-pro.*$/i, transform: () => 'Gemini 2.5 Pro' },
  { pattern: /^gemini-2\.0-flash.*$/i, transform: () => 'Gemini 2.0 Flash' },
  { pattern: /^gemini-(\d+\.?\d*)-flash.*$/i, transform: (m) => `Gemini ${m[1]} Flash` },
  { pattern: /^gemini-(\d+\.?\d*)-pro.*$/i, transform: (m) => `Gemini ${m[1]} Pro` },
  { pattern: /^gemini-pro.*$/i, transform: () => 'Gemini Pro' },
  { pattern: /^gemini-ultra.*$/i, transform: () => 'Gemini Ultra' },
  
  // Llama models
  { pattern: /^llama-(\d+\.?\d*)-(\d+)b.*$/i, transform: (m) => `Llama ${m[1]} ${m[2]}B` },
  { pattern: /^llama-(\d+)-maverick.*$/i, transform: (m) => `Llama ${m[1]} Maverick` },
  { pattern: /^llama-guard.*$/i, transform: () => 'Llama Guard' },
  { pattern: /^llama-(\d+).*$/i, transform: (m) => `Llama ${m[1]}` },
  { pattern: /^codellama.*$/i, transform: () => 'Code Llama' },
  
  // Mistral models
  { pattern: /^mistral-large.*$/i, transform: () => 'Mistral Large' },
  { pattern: /^mistral-medium.*$/i, transform: () => 'Mistral Medium' },
  { pattern: /^mistral-small.*$/i, transform: () => 'Mistral Small' },
  { pattern: /^mistral-nemo.*$/i, transform: () => 'Mistral Nemo' },
  { pattern: /^mixtral-8x22b.*$/i, transform: () => 'Mixtral 8x22B' },
  { pattern: /^mixtral-8x7b.*$/i, transform: () => 'Mixtral 8x7B' },
  { pattern: /^codestral.*$/i, transform: () => 'Codestral' },
  { pattern: /^pixtral.*$/i, transform: () => 'Pixtral' },
  
  // Grok models
  { pattern: /^grok-(\d+)-vision.*$/i, transform: (m) => `Grok ${m[1]} Vision` },
  { pattern: /^grok-(\d+)-mini.*$/i, transform: (m) => `Grok ${m[1]} Mini` },
  { pattern: /^grok-(\d+)$/i, transform: (m) => `Grok ${m[1]}` },
  { pattern: /^grok-beta.*$/i, transform: () => 'Grok Beta' },
  
  // Command models (Cohere)
  { pattern: /^command-r-plus.*$/i, transform: () => 'Command R+' },
  { pattern: /^command-r$/i, transform: () => 'Command R' },
  { pattern: /^command-light.*$/i, transform: () => 'Command Light' },
  { pattern: /^command$/i, transform: () => 'Command' },
  
  // Qwen models
  { pattern: /^qwen-(\d+\.?\d*)-turbo.*$/i, transform: (m) => `Qwen ${m[1]} Turbo` },
  { pattern: /^qwen-(\d+\.?\d*)-plus.*$/i, transform: (m) => `Qwen ${m[1]} Plus` },
  { pattern: /^qwen-max.*$/i, transform: () => 'Qwen Max' },
  { pattern: /^qwen-(\d+)b.*$/i, transform: (m) => `Qwen ${m[1]}B` },
  { pattern: /^qwq.*$/i, transform: () => 'QwQ' },
  
  // Yi models
  { pattern: /^yi-(\d+)b.*$/i, transform: (m) => `Yi ${m[1]}B` },
  { pattern: /^yi-large.*$/i, transform: () => 'Yi Large' },
  { pattern: /^yi-lightning.*$/i, transform: () => 'Yi Lightning' },
  
  // Other notable models
  { pattern: /^dbrx.*$/i, transform: () => 'DBRX' },
  { pattern: /^inflection-(\d+).*$/i, transform: (m) => `Inflection ${m[1]}` },
  { pattern: /^phi-(\d+).*$/i, transform: (m) => `Phi-${m[1]}` },
  { pattern: /^sonar.*$/i, transform: () => 'Sonar' },
];

/**
 * Extract the actual model provider from a full model ID.
 * For example: "openrouter/anthropic/claude-sonnet-4" -> "anthropic"
 */
export function extractModelProvider(fullModelId: string): string {
  // Split by slashes to get path segments
  const segments = fullModelId.split('/');
  
  // Look for known provider aliases in the path segments (skip first segment which is the API provider)
  for (let i = 1; i < segments.length; i++) {
    const segment = segments[i].toLowerCase();
    if (PROVIDER_ALIASES[segment]) {
      return PROVIDER_ALIASES[segment];
    }
  }
  
  // If no known provider found in path, check the model name itself
  const modelName = segments[segments.length - 1].toLowerCase();
  
  // Check for provider prefixes in model name
  for (const [alias, provider] of Object.entries(PROVIDER_ALIASES)) {
    if (modelName.startsWith(alias.toLowerCase())) {
      return provider;
    }
  }
  
  // Special cases based on model naming patterns
  if (modelName.includes('claude')) return 'anthropic';
  if (modelName.includes('gpt') || modelName.startsWith('o1') || modelName.startsWith('o3') || modelName.startsWith('o4')) return 'openai';
  if (modelName.includes('gemini')) return 'google';
  if (modelName.includes('llama')) return 'meta';
  if (modelName.includes('mistral') || modelName.includes('mixtral')) return 'mistral';
  if (modelName.includes('grok')) return 'xai';
  if (modelName.includes('command')) return 'cohere';
  if (modelName.includes('deepseek')) return 'deepseek';
  
  // Default: use the first segment as provider
  return segments[0].toLowerCase();
}

/**
 * Extract just the model name portion from a full model ID.
 * For example: "openrouter/anthropic/claude-sonnet-4" -> "claude-sonnet-4"
 */
export function extractModelName(fullModelId: string): string {
  const segments = fullModelId.split('/');
  return segments[segments.length - 1];
}

/**
 * Create a friendly display name for a model.
 * For example: "openrouter/anthropic/claude-sonnet-4" -> "Claude Sonnet 4"
 */
export function getFriendlyModelName(fullModelId: string): string {
  const modelName = extractModelName(fullModelId);
  
  // Try each pattern transformation
  for (const { pattern, transform } of MODEL_NAME_PATTERNS) {
    const match = modelName.match(pattern);
    if (match) {
      return transform(match);
    }
  }
  
  // Fallback: capitalize and clean up the model name
  return modelName
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/(\d+)b\b/gi, '$1B') // Ensure B is uppercase for billion
    .trim();
}

/**
 * Popular models that should appear at the top of the model selector.
 * These are the most commonly used and recommended models.
 */
export const POPULAR_MODELS = [
  // Anthropic latest
  'anthropic/claude-sonnet-4',
  'anthropic/claude-sonnet-4-20250514',
  'anthropic/claude-opus-4',
  // OpenAI latest  
  'openai/gpt-4o',
  'openai/gpt-4o-mini',
  'openai/o1',
  'openai/o3-mini',
  // Also match OpenRouter variants
  'openrouter/anthropic/claude-sonnet-4',
  'openrouter/anthropic/claude-sonnet-4-20250514',
  'openrouter/anthropic/claude-opus-4',
  'openrouter/openai/gpt-4o',
  'openrouter/openai/gpt-4o-mini',
  'openrouter/openai/o1',
  'openrouter/openai/o3-mini',
];

/**
 * Check if a model is in the popular models list.
 */
export function isPopularModel(fullModelId: string): boolean {
  // Direct match
  if (POPULAR_MODELS.includes(fullModelId)) {
    return true;
  }
  
  // Check if any popular model is a suffix of this model ID
  // This handles cases like "provider/openrouter/anthropic/claude-sonnet-4"
  for (const popular of POPULAR_MODELS) {
    if (fullModelId.endsWith(popular) || fullModelId.endsWith('/' + popular.split('/').slice(-1)[0])) {
      return true;
    }
  }
  
  return false;
}
